import { CHAT_CONVERSATION_RESET_EVENT } from '../../chatfe/chat/conversation-lifecycle.js';
import { createBookingComposer } from './bookingComposer.js';

import {
  createBookingSelect,
  isBookingReplyPending,
  styleBookingPicker,
} from './bookingPickerUi.js';

interface LocationPickerChat {
  _drawer?: { inputEl?: HTMLTextAreaElement };
  sendMessage: (text: string) => void;
  sendAction: (action: { title: string; type: string; payload: Record<string, unknown> }) => void;
  on: (event: 'error', handler: () => void) => () => void;
}

export function mountBookingLocationPicker(chat: unknown): () => void {
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
    return () => {};
  }

  const widget = chat as LocationPickerChat;
  let step:
    | 'city'
    | 'branch_selection'
    | 'brand_model'
    | 'browse_vehicle_brand'
    | 'browse_branch_city'
    | undefined;
  let intent: string | undefined;
  let vehicles: Array<{ brand: string; models: string[] }> = [];
  let options: Array<{ id: string; name: string; message?: string }> = [];
  let contextKey = '';
  let submitted = false;
  const textModeAllowed = false;
  let frame: number | undefined;
  let observedRoot: Node | undefined;
  const form = document.createElement('form');
  form.dataset.otokocBookingLocation = '1';
  form.setAttribute('aria-label', 'İl ve şube seçimi');
  styleBookingPicker(form);
  const fields = document.createElement('div');
  const select = createBookingSelect('İl', () => {
    model.setOptions([]);
    update();
  });
  const model = createBookingSelect('Model', () => update());
  fields.append(select.element);
  form.append(fields);
  const composer = createBookingComposer(form, () => {
    select.close();
    model.close();
  });
  let canSubmit = false;
  const hasVisibleBranchCards = (root: Node | undefined): boolean => {
    if (step !== 'branch_selection') return false;
    const scope =
      root instanceof Document || root instanceof ShadowRoot ? root : document.documentElement;
    return Array.from(scope.querySelectorAll<HTMLElement>('.otokoc-location-card')).some((card) => {
      const rect = card.getBoundingClientRect();
      return card.isConnected && rect.width > 0 && rect.height > 0;
    });
  };

  const update = () => {
    const busy = submitted || isBookingReplyPending(widget._drawer?.inputEl);
    const disabled = busy || !options.some((option) => option.id === select.value);
    if (select.disabled !== busy) select.disabled = busy;
    canSubmit = !disabled;
    model.disabled = true;
    const selected = options.find((option) => option.id === select.value);
    composer.setSelection(canSubmit ? (selected?.message ?? selected?.name ?? select.value) : '');
  };
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    update();
    const selected = options.find((option) => option.id === select.value);
    if (!step || !canSubmit || !selected) return;
    composer.clearDraft();
    submitted = true;
    sync();
    if (step === 'brand_model' || step === 'city') {
      widget.sendMessage(selected.name);
    } else if (step === 'browse_vehicle_brand' || step === 'browse_branch_city') {
      widget.sendMessage(selected.message ?? selected.name);
    } else {
      widget.sendAction({
        title: selected.name,
        type: 'inputText',
        payload: {
          text: selected.name,
          location_id: selected.id,
          branch_name: selected.name,
        },
      });
    }
  });
  const sync = () => {
    const input = widget._drawer?.inputEl;
    const root = input?.getRootNode();
    if (root && root !== observedRoot) {
      observedRoot = root;
      rootObserver.disconnect();
      rootObserver.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'class'],
      });
    }
    const area = input?.closest('[data-gengage-part="chat-input-area"]');
    if (
      !step ||
      !options.length ||
      submitted ||
      isBookingReplyPending(input) ||
      !area?.isConnected ||
      hasVisibleBranchCards(root)
    ) {
      select.close();
      model.close();
      composer.detach();
      return;
    }
    composer.setTextModeEnabled(textModeAllowed);
    composer.attach(input!);
    update();
  };
  const schedule = () => {
    if (frame !== undefined) return;
    frame = requestAnimationFrame(() => {
      frame = undefined;
      sync();
    });
  };
  const rootObserver = new MutationObserver(schedule);
  const hostObserver = new MutationObserver(schedule);
  hostObserver.observe(document.documentElement, { childList: true, subtree: true });
  const reset = (event?: Event) => {
    if (event?.type === CHAT_CONVERSATION_RESET_EVENT) composer.clearDraft();
    step = undefined;
    intent = undefined;
    options = [];
    contextKey = '';
    submitted = false;
    select.value = '';
    model.value = '';
    select.close();
    model.close();
    composer.detach();
  };
  const onMetadata = (event: Event) => {
    const payload = (event as CustomEvent).detail?.payload;
    if (!payload || !('panel' in payload)) return;
    const state = payload.panel?.agent_thread_state;
    const booking = state?.booking;
    const browseSelection = state?.browse_selection;
    if (
      state?.mode === 'browse' &&
      (browseSelection?.kind === 'vehicle_brand' || browseSelection?.kind === 'branch_city') &&
      Array.isArray(browseSelection.options)
    ) {
      step =
        browseSelection.kind === 'vehicle_brand' ? 'browse_vehicle_brand' : 'browse_branch_city';
      intent = undefined;
      vehicles = [];
      options = browseSelection.options.flatMap(
        (value: { id?: unknown; name?: unknown; message?: unknown }) =>
          typeof value?.id === 'string' && typeof value.name === 'string' && value.id && value.name
            ? [
                {
                  id: value.id,
                  name: value.name,
                  message:
                    typeof value.message === 'string' && value.message ? value.message : value.name,
                },
              ]
            : []
      );
      const nextKey = JSON.stringify([step, options]);
      if (contextKey !== nextKey) {
        contextKey = nextKey;
        const name = browseSelection.kind === 'vehicle_brand' ? 'Marka' : 'İl';
        form.setAttribute(
          'aria-label',
          browseSelection.kind === 'vehicle_brand' ? 'Marka seçimi' : 'İl seçimi'
        );
        fields.className = '';
        model.element.remove();
        model.setOptions([]);
        select.setOptions(options, name);
      }
      submitted = false;
      schedule();
      return;
    }
    if (
      state?.mode !== 'booking_active' ||
      !['city', 'branch_selection', 'brand_model'].includes(booking?.next_step)
    ) {
      reset();
      return;
    }
    step = booking.next_step;
    intent = typeof booking.intent === 'string' ? booking.intent : undefined;
    vehicles = Array.isArray(booking.vehicle_options)
      ? booking.vehicle_options
          .filter(
            (value: { brand?: unknown; models?: unknown }) =>
              typeof value?.brand === 'string' && Array.isArray(value.models)
          )
          .map((value: { brand: string; models: unknown[] }) => ({
            brand: value.brand,
            models: value.models.filter(
              (name): name is string => typeof name === 'string' && !!name
            ),
          }))
      : [];
    const rawOptions =
      step === 'brand_model'
        ? vehicles.map((vehicle) => ({ id: vehicle.brand, name: vehicle.brand }))
        : step === 'city'
          ? booking.location_options?.cities
          : booking.location_options?.branches;
    options = Array.isArray(rawOptions)
      ? rawOptions.flatMap((value: unknown) => {
          if (step === 'city')
            return typeof value === 'string' && value.trim() ? [{ id: value, name: value }] : [];
          const branch = value as { id?: unknown; name?: unknown } | null;
          return branch &&
            typeof branch.id === 'string' &&
            typeof branch.name === 'string' &&
            branch.id &&
            branch.name
            ? [{ id: branch.id, name: branch.name }]
            : [];
        })
      : [];
    const nextKey = JSON.stringify([
      booking.cycleId,
      intent,
      step,
      booking.fields?.city,
      booking.fields?.brand_model,
      options,
      vehicles,
    ]);
    if (contextKey !== nextKey) {
      contextKey = nextKey;
      const name = step === 'city' ? 'İl' : step === 'brand_model' ? 'Marka' : 'Şube';
      form.setAttribute(
        'aria-label',
        step === 'brand_model' ? 'Marka seçimi' : 'İl ve şube seçimi'
      );
      fields.className = '';
      model.element.remove();
      model.setOptions([]);
      select.setOptions(options, name);
    }
    submitted = false;
    schedule();
  };
  const stopError = widget.on('error', () => {
    submitted = false;
    schedule();
  });
  window.addEventListener('gengage:chat:metadata', onMetadata);
  window.addEventListener('gengage:chat:open', schedule);
  window.addEventListener(CHAT_CONVERSATION_RESET_EVENT, reset);
  schedule();
  return () => {
    if (frame !== undefined) cancelAnimationFrame(frame);
    rootObserver.disconnect();
    hostObserver.disconnect();
    stopError();
    select.close();
    model.close();
    composer.detach();
    window.removeEventListener('gengage:chat:metadata', onMetadata);
    window.removeEventListener('gengage:chat:open', schedule);
    window.removeEventListener(CHAT_CONVERSATION_RESET_EVENT, reset);
  };
}
